import type { ReservationStatus } from "@vidimo/shared/enums";
import type { ReactNode } from "react";
import { useTranslations } from "use-intl";
import {
  cn,
  getReservationStatusLabel,
  reservationStatusColors,
} from "@/lib/utils";

/**
 * `sm` is the chip inside a list row or a summary line; `md` the call
 * screens' wider one; `lg` the single pill at the top of a stay's own page.
 */
const SIZES = {
  lg: "px-3 py-1.5 text-sm",
  md: "items-center gap-1.5 px-3 py-1 text-xs",
  sm: "px-2 py-0.5 text-xs",
} as const;

/**
 * A reservation's status, in the colour the rest of the app uses for it.
 *
 * The fallback matters: `reservationStatusColors` is keyed by the enum, so a
 * status added to the database before this map catches up renders neutral
 * rather than unstyled.
 *
 * `children` replaces the label for the call screens, which prefix the room
 * ("Room 101 · Checked in"); everywhere else the label is the whole pill.
 */
export function ReservationStatusPill({
  children,
  className,
  size = "sm",
  status,
}: {
  children?: ReactNode;
  className?: string;
  size?: keyof typeof SIZES;
  status: ReservationStatus;
}) {
  const tLabels = useTranslations("labels");
  return (
    <span
      className={cn(
        "inline-flex shrink-0 rounded-full border font-medium",
        SIZES[size],
        reservationStatusColors[status]?.tailwind ??
          "border-border bg-muted text-muted-foreground",
        className
      )}
    >
      {children ?? getReservationStatusLabel(tLabels, status)}
    </span>
  );
}
